import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BreadcrumbsComponent, SignOutDialogComponent, SignOutChoice } from '../shared/components';
import { DevPortalSessionService } from '../core/auth/dev-portal-session.service';
import { startPortalLogin } from '../core/auth/start-portal-login';

interface NavLink {
  kind: 'link';
  label: string;
  route: string;
  icon: string;
}

interface NavSection {
  kind: 'section';
  label: string;
}

type NavEntry = NavLink | NavSection;

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    FlexLayoutModule,
    BreadcrumbsComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  readonly navItems: NavEntry[] = [
    { kind: 'link', label: 'Dashboard', route: '/dashboard', icon: 'dashboard' },
    { kind: 'link', label: 'Reports', route: '/reports', icon: 'assessment' },
    { kind: 'link', label: 'Queues', route: '/queues', icon: 'queue' },
    { kind: 'link', label: 'Document Search', route: '/documents', icon: 'search' },
    { kind: 'link', label: 'Integrations', route: '/integrations', icon: 'sync_alt' },
    { kind: 'section', label: 'Telemetry' },
    { kind: 'link', label: 'Correlation History', route: '/correlation-history', icon: 'timeline' },
    { kind: 'link', label: 'AS2', route: '/as2', icon: 'compare_arrows' },

    { kind: 'link', label: 'SFTP', route: '/sftp', icon: 'folder_zip' },
    { kind: 'link', label: 'Email', route: '/email', icon: 'mail' },
    { kind: 'link', label: 'WebAPI Invoices', route: '/webapi', icon: 'http' },
    { kind: 'link', label: 'Pi Orders', route: '/pi-orders', icon: 'shopping_cart' },
    { kind: 'link', label: 'Salesforce', route: '/salesforce', icon: 'cloud_upload' },
    { kind: 'link', label: 'Oracle Fusion', route: '/oracle-fusion', icon: 'account_balance' },
    { kind: 'section', label: 'Utilities' },
    { kind: 'link', label: 'SFTP Connectivity', route: '/sftp-connectivity', icon: 'lan' },
    { kind: 'link', label: 'Oracle Fusion Lookup', route: '/oracle-fusion-lookup', icon: 'receipt_long' },
    { kind: 'link', label: 'XSLT Tester', route: '/xslt-tester', icon: 'transform' },
    { kind: 'section', label: 'Config' },
    { kind: 'link', label: 'External Alerts', route: '/external-alerts', icon: 'notifications' },
  ];

  activeAccount: AccountInfo | null = null;
  devSessionActive = false;
  readonly isLocalDevAuth = environment.auth.useLocalDevAuth;
  private readonly destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private broadcastService: MsalBroadcastService,
    private router: Router,
    private dialog: MatDialog,
    private devSession: DevPortalSessionService,
  ) {}

  ngOnInit(): void {
    this.refreshActiveAccount();
    this.refreshDevSession();

    this.devSession.sessionChanges$.pipe(takeUntil(this.destroying$)).subscribe(() => {
      this.refreshDevSession();
    });

    this.broadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.refreshActiveAccount();

        if (this.activeAccount && this.router.url === '/') {
          this.router.navigate(['/dashboard']);
        }
      });
  }

  login(): void {
    startPortalLogin(this.authService, this.router, this.devSession);
  }

  devLogout(): void {
    this.devSession.clearSession();
    this.router.navigate(['/welcome']);
  }

  logout(): void {
    if (this.isLocalDevAuth) {
      return;
    }

    const dialogRef = this.dialog.open(SignOutDialogComponent, {
      width: '420px',
    });

    dialogRef.afterClosed().subscribe(async (choice: SignOutChoice | undefined) => {
      if (choice === 'portal') {
        await this.authService.instance.clearCache();
        this.devSession.clearSession();
        this.activeAccount = null;
        this.router.navigate(['/welcome']);
      } else if (choice === 'all') {
        this.authService.logoutRedirect();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private refreshDevSession(): void {
    this.devSessionActive = this.isLocalDevAuth && this.devSession.hasSession();
  }

  private refreshActiveAccount(): void {
    if (this.isLocalDevAuth) {
      this.activeAccount = null;
      return;
    }

    const account =
      this.authService.instance.getActiveAccount() ??
      this.authService.instance.getAllAccounts()[0] ??
      null;

    this.activeAccount = account;

    if (account && !this.authService.instance.getActiveAccount()) {
      this.authService.instance.setActiveAccount(account);
    }
  }
}
